import { ethers, Contract } from 'ethers'
import { getMulticallContract } from 'utils/contractHelpers'
import { MultiCallResponse } from './types'

export interface Call {
  address: string // Address of the contract
  name: string // Function name on the contract (example: balanceOf)
  params?: any[] // Function params
}
export type MulticallV2Encoded = [contract: Contract, fnName: string, encodedData: string]

interface MulticallOptions {
  requireSuccess?: boolean
}

const multicall = async <T = any>(abi: any[], calls: Call[]): Promise<T> => {
  try {
    const multi = getMulticallContract()
    const itf = new ethers.utils.Interface(abi)

    const calldata = calls.map((call) => [call.address.toLowerCase(), itf.encodeFunctionData(call.name, call.params)])
    const { returnData } = await multi.aggregate(calldata)

    const res = returnData.map((call, i) => itf.decodeFunctionResult(calls[i].name, call))

    return res
  } catch (error) {
    throw new Error(error)
  }
}

export default multicall

/**
 * Multicall V2 uses the new "tryAggregate" function. It is different in 2 ways
 *
 * 1. If "requireSuccess" is false multicall will not bail out if one of the calls fails
 * 2. The return includes a boolean whether the call was successful e.g. [wasSuccessful, callResult]
 */
export const multicallv2 = async <T = any>(
  abi: any[],
  calls: Call[],
  options: MulticallOptions = { requireSuccess: true },
  signer?: ethers.Signer | ethers.providers.Provider,
): Promise<MultiCallResponse<T>> => {
  const { requireSuccess } = options
  const multi = getMulticallContract(signer)
  const itf = new ethers.utils.Interface(abi)

  const calldata = calls.map((call) => [call.address.toLowerCase(), itf.encodeFunctionData(call.name, call.params)])
  const returnData = await multi.tryAggregate(requireSuccess, calldata)
  const res = returnData.map((call, i) => {
    const [result, data] = call
    return result ? itf.decodeFunctionResult(calls[i].name, data) : null
  })

  return res
}

/**
 * same as multicallv2, but better typing support for caller side
 *
 * @param calls, an array of tuples: [contract, 'functionName', contract.interface.encodeFunctionData('functionName', [...params])]
 *
 * @example
 *   import { multicallv2Encoded } from 'utils/multicall'
 *   import type { AwaitedReturnType } from 'utils/types'
 *
 *   const boostAllocationPoolInfos = await multicallv2Encoded<
 *     AwaitedReturnType<typeof craftsmanContract['poolInfo']>[]
 *   >(
 *     boostableFarmInfos.map(({ info }) => [
 *       craftsmanContract,
 *       'poolInfo',
 *       craftsmanContract.interface.encodeFunctionData('poolInfo', [info.depositTokenPid]),
 *     ]),
 *   )
 */
export const multicallv2Encoded = async <T extends any[]>(
  calls: MulticallV2Encoded[],
  signer?: ethers.Signer | ethers.providers.Provider,
  options = { requireSuccess: true, autoUnwrapResult: true },
): Promise<T> => {
  const { requireSuccess } = options
  const multi = getMulticallContract(signer)

  const calldata = calls.map(([contract, _fnName, encodedData]) => [contract.address.toLowerCase(), encodedData])
  const returnData = await multi.tryAggregate(requireSuccess, calldata)
  const res = returnData.map((callRes: [result: any, data: any], i: number) => {
    const [result, data] = callRes
    if (!result) return null

    const [contract, fnName] = calls[i]
    const decodedResult = contract.interface.decodeFunctionResult(fnName, data)

    if (options.autoUnwrapResult && Array.isArray(decodedResult) && decodedResult.length === 1) {
      return decodedResult[0]
    }
    return decodedResult
  })

  return res
}

export interface MultiEncodedcallsback<T = any> {
  calls: MulticallV2Encoded[]
  callback: (multiCallResponses: any[]) => T
}

/**
 * run multiple complex multicalls in one multicall
 *
 * each element's `callback` of `callsWithCallbacks` will get the response of its `calls`,
 * returns array of return value from each `callback`
 *
 * @example
 *   const result = await runMultiEncodedcallsbacks(
 *     [0, 1, 2, 3].map(pid => {
 *       if (pid === 0) {
 *         return {
 *           calls: [
 *             [someContract, 'functionA', someContract.interface.encodeFunctionData('functionA', [pid])],
 *           ],
 *           callback: ([resultFromFunctionA]) => {
 *             return new BigNumber(resultFromFunctionA.toString())
 *           }
 *         }
 *       }
 *
 *       return {
 *         calls: [
 *           [someContract, 'functionB', someContract.interface.encodeFunctionData('functionB', [pid])],
 *           [someContract, 'functionC', someContract.interface.encodeFunctionData('functionC', [pid])],
 *         ],
 *         callback: ([resultFromFunctionB, resultFromFunctionC]) => {
 *           return new BigNumber(resultFromFunctionB.toString()).plus(new BigNumber(resultFromFunctionC.toString()))
 *         }
 *       }
 *     })
 *   )
 *   console.log(result) // => [resultFromFunctionA, resultFromFunctionB + resultFromFunctionC, ...]
 *
 * see state/farms/fetchFarms.ts and state/farms/fetchFarm.ts for real-world complex case
 */
export const runMultiEncodedcallsbacks = async <T = any>(callsWithCallbacks: MultiEncodedcallsback<T>[]) => {
  const multicallResponses = await multicallv2Encoded(callsWithCallbacks.flatMap((cc) => cc.calls))

  return callsWithCallbacks.map((cc) => {
    return cc.callback(multicallResponses.splice(0, cc.calls.length))
  })
}
